import ReactiveDialog from "../../common/ReactiveDialog";
import { Box, Typography, MenuItem } from "@mui/material";
import {
  useGetFamilyDetails,
  useRematchAdvisor,
} from "../../../services/advisor";
import Loading from "../../common/Loading";
import { useEffect, useState } from "react";
import Select from "components/common/Select";
import { PlatformFeeSchedule } from "protogen/billing_pb";
import AdvisorSelect from "components/creation/AdvisorSelect";
import PlatformFeeSelect from "../PlatformFeeSelect";

type Props = {
  familyRef: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onUpdate: () => void;
};

const RematchAdvisorContent = ({
  familyData,
  onAdvisorChange,
  onEffectiveDateChange,
  onPlatformFeeChange,
  selectedAdvisorRef,
  selectedEffectiveDate,
  selectedPlatformFee,
  disabled = false,
}: {
  familyData: any;
  onAdvisorChange: (advisorRef: string) => void;
  onEffectiveDateChange: (date: string) => void;
  onPlatformFeeChange: (fee: PlatformFeeSchedule) => void;
  selectedAdvisorRef: string;
  selectedEffectiveDate: string;
  selectedPlatformFee: PlatformFeeSchedule;
  disabled?: boolean;
}) => {
  const billingPeriodStart = familyData.planUtilization?.periodStartDate
    ? new Date(familyData.planUtilization.periodStartDate).toLocaleDateString(
        "en-US",
        {
          day: "numeric",
          month: "long",
          year: "numeric",
          timeZone: "UTC",
        },
      )
    : null;

  const billingPeriodEnd = familyData.planUtilization?.periodEndDate
    ? new Date(familyData.planUtilization.periodEndDate).toLocaleDateString(
        "en-US",
        {
          day: "numeric",
          month: "long",
          year: "numeric",
          timeZone: "UTC",
        },
      )
    : null;

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Box>
        <Typography variant="bodyHeavy" gutterBottom>
          New Advisor
        </Typography>
        <AdvisorSelect
          title="Select new advisor"
          disabled={disabled}
          selectedAdvisor={selectedAdvisorRef}
          onChange={(r) => onAdvisorChange(r || "")}
        />
      </Box>

      <Box>
        <Typography variant="bodyHeavy" gutterBottom>
          Effective Date
        </Typography>
        <Select
          variant="outlined"
          placeholder="Select effective date"
          displayEmpty
          disabled={disabled}
          value={selectedEffectiveDate}
          onChange={(e) => onEffectiveDateChange(e.target.value)}
          sx={{
            "& .MuiInputBase-input": {
              marginTop: "0px !important",
            },
            width: "100%",
            height: "60px",
          }}
        >
          <MenuItem key={""} value={""} disabled>
            Select effective date
          </MenuItem>
          {billingPeriodStart && (
            <MenuItem value="period-start">
              {billingPeriodStart} (this cycle)
            </MenuItem>
          )}
          {billingPeriodEnd && (
            <MenuItem value="period-end">
              {billingPeriodEnd} (next cycle)
            </MenuItem>
          )}
        </Select>
      </Box>

      <Box>
        <Typography variant="bodyHeavy" gutterBottom>
          New Fee Schedule
        </Typography>
        <PlatformFeeSelect
          value={selectedPlatformFee}
          onUpdate={(p) => p && onPlatformFeeChange(p)}
          disabled={disabled}
          fullWidth
        />
      </Box>
    </Box>
  );
};

export default ({ familyRef, open, setOpen, onUpdate }: Props) => {
  const {
    data: familyData,
    request: requestFamily,
    loading: loadingFamily,
  } = useGetFamilyDetails();

  const [selectedAdvisorRef, setSelectedAdvisorRef] = useState<string>("");
  const [selectedEffectiveDate, setSelectedEffectiveDate] =
    useState<string>("");
  const [selectedPlatformFee, setSelectedPlatformFee] =
    useState<PlatformFeeSchedule>(
      PlatformFeeSchedule.SCHEDULE_LEAD_GEN_45_45_45_25,
    );

  const { request: requestRematch, loading: loadingRematch } =
    useRematchAdvisor();

  useEffect(() => {
    const getFamily = async () => {
      const resp = await requestFamily({ familyRef });
      if (resp?.family) {
        setSelectedAdvisorRef(resp.family.advisorRef);
      }
    };
    if (open) {
      getFamily();
    }
  }, [open, familyRef]);

  const onRematch = async () => {
    if (!selectedAdvisorRef || !selectedEffectiveDate || !familyData?.family)
      return;

    const resp = await requestRematch({
      familyRef,
      newAdvisorRef: selectedAdvisorRef,
      effectiveDate: selectedEffectiveDate,
      platformFeeSchedule: selectedPlatformFee,
    });

    if (resp?.family) {
      onUpdate();
    }
  };

  const isFormValid = !!(
    selectedAdvisorRef &&
    selectedEffectiveDate &&
    selectedPlatformFee &&
    familyData?.family &&
    familyData?.family?.advisorRef !== selectedAdvisorRef &&
    !loadingFamily &&
    !loadingRematch
  );

  return (
    <ReactiveDialog
      open={open}
      onClose={() => setOpen(false)}
      title="Rematch Advisor"
      primaryActionName="Rematch Advisor"
      primaryActionEnabled={isFormValid}
      primaryAction={onRematch}
      fullWidthSize="sm"
    >
      {loadingFamily ? (
        <Box display="flex" justifyContent="center" p={3}>
          <Loading />
        </Box>
      ) : familyData?.family ? (
        <RematchAdvisorContent
          familyData={familyData}
          onAdvisorChange={setSelectedAdvisorRef}
          onEffectiveDateChange={setSelectedEffectiveDate}
          onPlatformFeeChange={setSelectedPlatformFee}
          selectedAdvisorRef={selectedAdvisorRef}
          selectedEffectiveDate={selectedEffectiveDate}
          selectedPlatformFee={selectedPlatformFee}
          disabled={loadingRematch}
        />
      ) : null}
    </ReactiveDialog>
  );
};
